import { useEffect, useState } from "react";
import { UserPlus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";

/* Shared "Add Friend" pill used both in-call (VideoCall.jsx) and on the
   Session Review header (SessionReview.jsx).

   Props:
     - peerUserId   : peer's supabase_sub / LiveKit identity (required)
     - peerUsername : peer's @username, used only for the toast copy
     - className    : optional extra Tailwind classes for the wrapper button
     - testid       : override the default data-testid ("add-friend-btn")

   States rendered (friendship is now INSTANT — no pending/request flow):
     loading                        → spinner ("…")
     self                           → hidden (returns null)
     none                           → clickable "+ Add Friend"
     sending                        → spinner + "Adding…"
     friends                        → static "✓ Friends"
*/

const BASE =
  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors";

const STYLE_ACTIVE =
  "border border-[#E52525]/40 bg-[#E52525]/10 text-white hover:bg-[#E52525]/20";
const STYLE_MUTED =
  "border border-white/10 bg-transparent text-zinc-400 cursor-default";
const STYLE_FRIENDS =
  "border border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E] cursor-default";

export function AddFriendButton({
  peerUserId,
  peerUsername,
  className = "",
  testid = "add-friend-btn",
}) {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!peerUserId) {
      setStatus("loading");
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/friends/status/${encodeURIComponent(peerUserId)}`);
        if (!alive) return;
        setStatus(r.data?.status || "none");
      } catch {
        // If we can't fetch status, still let the user try to add — the POST
        // itself will surface any hard error (409, 404 …).
        if (alive) setStatus("none");
      }
    })();
    return () => {
      alive = false;
    };
  }, [peerUserId]);

  const handleClick = async () => {
    if (status !== "none") return;
    setStatus("sending");
    try {
      await api.post("/friends/request", { user_id: peerUserId });
      // Backend now creates friendships instantly — reflect that in the UI.
      setStatus("friends");
      toast.success(
        peerUsername ? `You and ${peerUsername} are now friends.` : "You are now friends."
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add friend.";
      // 409 → race / already friends. Re-query to reflect current state.
      if (err?.response?.status === 409) {
        try {
          const r = await api.get(`/friends/status/${encodeURIComponent(peerUserId)}`);
          setStatus(r.data?.status || "friends");
        } catch {
          setStatus("friends");
        }
      } else {
        setStatus("none");
      }
      toast.error(msg);
    }
  };

  if (!peerUserId || status === "self") return null;

  if (status === "loading") {
    return (
      <span
        data-testid={`${testid}-loading`}
        className={`${BASE} ${STYLE_MUTED} ${className}`}
      >
        <Loader2 size={12} className="animate-spin" />
        …
      </span>
    );
  }

  if (status === "friends") {
    return (
      <span
        data-testid={`${testid}-friends`}
        className={`${BASE} ${STYLE_FRIENDS} ${className}`}
      >
        <Check size={12} /> Friends
      </span>
    );
  }

  if (status === "sending") {
    return (
      <span
        data-testid={`${testid}-sending`}
        className={`${BASE} ${STYLE_MUTED} ${className}`}
      >
        <Loader2 size={12} className="animate-spin" />
        Adding…
      </span>
    );
  }

  // status === "none" (or any legacy pending_* value — treat as clickable so
  // users on fresh builds immediately escape the old request state)
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={handleClick}
      className={`${BASE} ${STYLE_ACTIVE} ${className}`}
    >
      <UserPlus size={12} />
      Add Friend
    </button>
  );
}

export default AddFriendButton;
